'use client';
import React, { useState, useMemo, useEffect } from 'react';
import { Search, SlidersHorizontal, X, ChevronDown, MapPin, Grid3X3, List, ShieldCheck } from 'lucide-react';
import { MOCK_PROPERTIES, type ListingMode } from './mockData';
import PropertyCard from './PropertyCard';
import FilterSidebar from './FilterSidebar';
import ContactUnlockModal from './ContactUnlockModal';
import BadgePurchaseModal from '@/components/BadgePurchaseModal';
import type { Property } from './mockData';
import { useLocale } from '@/contexts/LocaleContext';
import { useAuth } from '@/contexts/AuthContext';

export type SortOption = 'newest' | 'price_asc' | 'price_desc' | 'area_desc' | 'most_viewed';

export default function PropertyListingsClient() {
  const { locale } = useLocale();
  const { user } = useAuth();
  const cities = locale.cities;
  const allCityLabel = locale.cityLabel;

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCity, setSelectedCity] = useState(allCityLabel);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedMode, setSelectedMode] = useState<'all' | ListingMode>('all');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [furnished, setFurnished] = useState<boolean | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterOpen, setFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [unlockModal, setUnlockModal] = useState<Property | null>(null);
  const [badgeModal, setBadgeModal] = useState<'seller' | 'buyer' | null>(null);
  const [ownedBadges, setOwnedBadges] = useState<{ seller: boolean; buyer: boolean }>({ seller: false, buyer: false });
  const itemsPerPage = 9;

  // Reset city selection when locale changes
  useEffect(() => {
    setSelectedCity(allCityLabel);
    setCurrentPage(1);
  }, [allCityLabel]);

  const isArabic = locale.language === 'ar';

  const filtered = useMemo(() => {
    let list = [...MOCK_PROPERTIES];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.city.includes(q) ||
        p.district.includes(q)
      );
    }
    if (selectedCity !== allCityLabel) list = list.filter(p => p.city === selectedCity);
    if (selectedType !== 'all') list = list.filter(p => p.type === selectedType);
    if (selectedMode !== 'all') list = list.filter(p => p.mode === selectedMode);
    if (priceMin) list = list.filter(p => p.price >= Number(priceMin));
    if (priceMax) list = list.filter(p => p.price <= Number(priceMax));
    if (furnished !== null) list = list.filter(p => p.furnished === furnished);

    switch (sortBy) {
      case 'price_asc': list.sort((a, b) => a.price - b.price); break;
      case 'price_desc': list.sort((a, b) => b.price - a.price); break;
      case 'area_desc': list.sort((a, b) => b.area - a.area); break;
      case 'most_viewed': list.sort((a, b) => b.views - a.views); break;
      default: list.sort((a, b) => new Date(b.listedAt).getTime() - new Date(a.listedAt).getTime());
    }

    return list;
  }, [searchQuery, selectedCity, selectedType, selectedMode, priceMin, priceMax, furnished, sortBy, allCityLabel]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const activeFilterCount = [
    selectedCity !== allCityLabel,
    selectedType !== 'all',
    selectedMode !== 'all',
    !!priceMin,
    !!priceMax,
    furnished !== null,
  ].filter(Boolean).length;

  const clearFilters = () => {
    setSelectedCity(allCityLabel);
    setSelectedType('all');
    setSelectedMode('all');
    setPriceMin('');
    setPriceMax('');
    setFurnished(null);
    setCurrentPage(1);
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 py-8">
      {/* Hero Search Bar */}
      <div className="bg-gradient-to-l from-primary-600 to-primary-500 rounded-2xl p-5 md:p-8 mb-8 shadow-lg">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-800 text-white mb-1 md:mb-2">
          {isArabic ? 'ابحث عن عقارك المثالي' : 'Find Your Perfect Property'}
        </h1>
        <p className="text-primary-100 text-xs sm:text-sm mb-4 md:mb-5">
          {isArabic
            ? `أكثر من ${MOCK_PROPERTIES.length} إعلان متاح — شقق، فلل، مكاتب وأكثر`
            : `Over ${MOCK_PROPERTIES.length} listings available — apartments, villas, offices & more`}
        </p>

        {/* Location badge */}
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1.5 bg-white/20 text-white text-xs font-500 px-3 py-1 rounded-full">
            <MapPin size={12} />
            {locale.country} · {locale.currencySymbol}
          </span>
        </div>

        {/* Mode Toggle */}
        <div className="flex items-center gap-2 mb-4">
          {(['all', 'sale', 'rent'] as const).map(mode => (
            <button
              key={`mode-${mode}`}
              onClick={() => { setSelectedMode(mode); setCurrentPage(1); }}
              suppressHydrationWarning
              className={`px-4 py-1.5 rounded-full text-sm font-600 transition-all duration-150 ${
                selectedMode === mode
                  ? 'bg-white text-primary-600' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {mode === 'all' ? (isArabic ?'الكل' : 'All')
                : mode === 'sale' ? (isArabic ?'للبيع' : 'For Sale')
                  : (isArabic ? 'للإيجار' : 'For Rent')}
            </button>
          ))}
        </div>

        {/* Search Row */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              placeholder={isArabic ? 'ابحث بالمنطقة أو المدينة أو نوع العقار...' : 'Search by area, city or property type...'}
              className="w-full pr-9 pl-4 py-2.5 rounded-xl text-sm bg-white text-gray-800 placeholder-gray-400 border-0 focus:ring-2 focus:ring-white/50 outline-none"
              dir={locale.dir}
            />
          </div>
          {/* City Quick Filter */}
          <div className="relative hidden sm:block">
            <MapPin size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <select
              value={selectedCity}
              onChange={e => { setSelectedCity(e.target.value); setCurrentPage(1); }}
              className="pr-8 pl-3 py-2.5 rounded-xl text-sm bg-white text-gray-700 border-0 focus:ring-2 focus:ring-white/50 outline-none appearance-none cursor-pointer min-w-[120px]"
              dir={locale.dir}
            >
              {cities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Verified Badges Section */}
      <div className="bg-white border border-gray-100 rounded-2xl p-5 mb-8 shadow-sm" dir="rtl">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={20} className="text-blue-500" />
          <h2 className="font-700 text-gray-900 text-base">شارات التوثيق</h2>
          <span className="text-xs text-gray-400 font-400">— احصل على شارة موثق وزد مصداقيتك</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Seller Badge */}
          <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <ShieldCheck size={20} className="text-blue-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-700 text-gray-900 text-sm">بائع موثق</span>
                  {ownedBadges.seller && (
                    <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full font-600">✓ لديك</span>
                  )}
                </div>
                <p className="text-xs text-gray-500">تظهر على إعلاناتك — تزيد الثقة</p>
                <p className="text-sm font-800 text-blue-600 mt-0.5">$5.00 USD</p>
              </div>
            </div>
            {ownedBadges.seller ? (
              <span className="flex items-center gap-1 text-xs text-blue-600 font-600 bg-blue-100 px-3 py-1.5 rounded-lg">
                <ShieldCheck size={13} /> موثق
              </span>
            ) : (
              <button
                onClick={() => user ? setBadgeModal('seller') : setBadgeModal('seller')}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-xs font-600 rounded-lg transition-all active:scale-95"
              >
                احصل عليها
              </button>
            )}
          </div>

          {/* Buyer Badge */}
          <div className="flex items-center justify-between bg-emerald-50 border border-emerald-100 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <ShieldCheck size={20} className="text-emerald-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-700 text-gray-900 text-sm">مشتري/مستأجر موثق</span>
                  {ownedBadges.buyer && (
                    <span className="bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full font-600">✓ لديك</span>
                  )}
                </div>
                <p className="text-xs text-gray-500">أثبت جديتك للبائعين</p>
                <p className="text-sm font-800 text-emerald-600 mt-0.5">$5.00 USD</p>
              </div>
            </div>
            {ownedBadges.buyer ? (
              <span className="flex items-center gap-1 text-xs text-emerald-600 font-600 bg-emerald-100 px-3 py-1.5 rounded-lg">
                <ShieldCheck size={13} /> موثق
              </span>
            ) : (
              <button
                onClick={() => setBadgeModal('buyer')}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-600 rounded-lg transition-all active:scale-95"
              >
                احصل عليها
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-3 text-center">
          💡 كشف بيانات التواصل مع البائع يكلف <strong>$10.00</strong> — ادفع مرة واحدة وتواصل مباشرة
        </p>
      </div>

      {/* Marketing Banner — No Agent Fees */}
      <div className="bg-gradient-to-l from-blue-700 to-green-600 rounded-2xl p-5 md:p-7 mb-8 shadow-lg text-right" dir="rtl">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-3xl">☕</span>
              <h2 className="text-white font-800 text-lg md:text-xl">تواصل مع البائع مباشرة — بدون وسيط وبدون عمولة!</h2>
            </div>
            <p className="text-white/90 text-sm leading-relaxed mb-3">
              لماذا تدفع آلاف الدراهم أو الريالات كعمولة لوكيل عقاري؟ في Directly، يمكنك الوصول إلى بيانات التواصل المباشرة مع مالك العقار بتكلفة رمزية — أقل من ثمن فنجان قهوة — وتتفاوض معه مباشرة دون أي وسيط.
            </p>
            <div className="flex flex-wrap gap-2">
              {[
                { icon: '✅', text: isArabic ? 'بدون عمولة وكيل' : 'No agent commission' },
                { icon: '📞', text: isArabic ? 'تواصل مباشر مع المالك' : 'Direct owner contact' },
                { icon: '💰', text: isArabic ? 'وفّر آلاف الريالات/الدراهم' : 'Save thousands' },
                { icon: '🔒', text: isArabic ? 'بيانات موثوقة ومحمية' : 'Verified & secure' },
              ].map(item => (
                <span key={item.text} className="bg-white/20 text-white text-xs font-600 px-3 py-1.5 rounded-full">
                  {item.icon} {item.text}
                </span>
              ))}
            </div>
          </div>
          <div className="flex-shrink-0 bg-white/15 rounded-xl p-4 text-center min-w-[140px]">
            <div className="text-4xl mb-1">🏠</div>
            <p className="text-white font-700 text-sm">{isArabic ? 'اكشف بيانات التواصل' : 'Reveal Contact Info'}</p>
            <p className="text-white/70 text-xs mt-1">{isArabic ? '$10 فقط' : '$10 only'}</p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setFilterOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 hover:border-primary-300 hover:bg-primary-50 text-sm font-500 text-gray-700 transition-all duration-150"
          >
            <SlidersHorizontal size={16} />
            {isArabic ? 'تصفية' : 'Filter'}
            {activeFilterCount > 0 && (
              <span className="bg-primary-500 text-white text-xs font-700 px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                {activeFilterCount}
              </span>
            )}
          </button>
          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-500 text-red-500 hover:bg-red-50 transition-colors"
            >
              <X size={14} />
              {isArabic ? 'مسح' : 'Clear'}
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Sort */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as SortOption)}
              className="appearance-none pr-8 pl-3 py-2 rounded-xl border border-gray-200 text-sm font-500 text-gray-700 bg-white focus:outline-none focus:border-primary-300 cursor-pointer"
              dir={locale.dir}
            >
              <option value="newest">{isArabic ? 'الأحدث' : 'Newest'}</option>
              <option value="price_asc">{isArabic ? 'السعر: الأقل' : 'Price: Low'}</option>
              <option value="price_desc">{isArabic ? 'السعر: الأعلى' : 'Price: High'}</option>
              <option value="area_desc">{isArabic ? 'المساحة: الأكبر' : 'Area: Largest'}</option>
              <option value="most_viewed">{isArabic ? 'الأكثر مشاهدة' : 'Most Viewed'}</option>
            </select>
            <ChevronDown size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>

          {/* View Mode */}
          <div className="flex items-center border border-gray-200 rounded-xl overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-primary-500 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
              title={isArabic ? 'عرض شبكي' : 'Grid view'}
            >
              <Grid3X3 size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-primary-500 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
              title={isArabic ? 'عرض قائمة' : 'List view'}
            >
              <List size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <p className="text-sm text-gray-500 mb-4">
        {isArabic
          ? `${filtered.length} نتيجة`
          : `${filtered.length} results`}
        {selectedCity !== allCityLabel && (
          <span className="text-primary-500 font-500"> · {selectedCity}</span>
        )}
      </p>

      {/* Property Grid/List */}
      {paginated.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-5xl mb-4">🏠</div>
          <p className="text-gray-500 text-lg font-500">
            {isArabic ? 'لا توجد نتائج مطابقة' : 'No matching results'}
          </p>
          <button onClick={clearFilters} className="mt-4 text-primary-500 text-sm hover:underline">
            {isArabic ? 'مسح الفلاتر' : 'Clear filters'}
          </button>
        </div>
      ) : (
        <div className={viewMode === 'grid' ?'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5' :'flex flex-col gap-4'
        }>
          {paginated.map(property => (
            <PropertyCard
              key={property.id}
              property={property}
              viewMode={viewMode}
              onUnlock={() => setUnlockModal(property)}
              ownerBadge={property.verified ? 'seller' : null}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-10">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-500 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {isArabic ? 'السابق' : 'Previous'}
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`w-9 h-9 rounded-xl text-sm font-600 transition-colors ${
                currentPage === page
                  ? 'bg-primary-500 text-white' :'border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-500 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {isArabic ? 'التالي' : 'Next'}
          </button>
        </div>
      )}

      {/* Filter Sidebar */}
      <FilterSidebar
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        selectedCity={selectedCity}
        setSelectedCity={setSelectedCity}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedMode={selectedMode}
        setSelectedMode={setSelectedMode}
        priceMin={priceMin}
        setPriceMin={setPriceMin}
        priceMax={priceMax}
        setPriceMax={setPriceMax}
        furnished={furnished}
        setFurnished={setFurnished}
        onClear={clearFilters}
      />

      {/* Contact Unlock Modal */}
      {unlockModal && (
        <ContactUnlockModal
          property={unlockModal}
          onClose={() => setUnlockModal(null)}
        />
      )}

      {/* Badge Purchase Modal */}
      {badgeModal && (
        <BadgePurchaseModal
          badgeType={badgeModal}
          onClose={() => setBadgeModal(null)}
          onSuccess={(type) => {
            setOwnedBadges(prev => ({ ...prev, [type]: true }));
            setBadgeModal(null);
          }}
        />
      )}
    </div>
  );
}